import { forwardRef, Ref, useImperativeHandle } from 'react'
import { Alert, Form as ANTDForm } from 'antd'
import Create from './Create'
import Update from './Update'
import Delete from './Delete'
import { useSBRMData } from '../../../modules/sbrm/hooks'
import {
  SBRMFormInterface,
  SBRMFormProps,
} from '../../../modules/sbrm/SBRMModel'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  createAnswer,
  deleteAnswer,
  updateAnswer,
} from '../../../store/AnswerReducer'

const Form = forwardRef(
  ({ isNested }: SBRMFormProps, ref: Ref<SBRMFormInterface>) => {
    useImperativeHandle(ref, () => ({
      handleCreate,
      handleUpdate,
      handleDelete,
      handleReset,
    }))

    const dispatch = useAppDispatch()
    const { action, metadata } = useSBRMData(isNested)
    const { error } = useAppSelector((state) => state.answer)

    const [createForm] = ANTDForm.useForm()
    const [updateForm] = ANTDForm.useForm()
    const [deleteForm] = ANTDForm.useForm()

    const handleCreate = async () =>
      await createForm.validateFields().then(async (result) => {
        const data = new FormData()
        const fields = ['automation', 'forFollower', 'message']
        fields.map((field) => {
          data.append(field, createForm.getFieldValue(field) ?? '')
        })
        if (createForm.getFieldValue('buttons')) {
          createForm
            .getFieldValue('buttons')
            .forEach((button: any, index: number) => {
              data.append(`buttons[${index}][title]`, button.title)
              data.append(`buttons[${index}][url]`, button.url)
            })
        }
        if (createForm.getFieldValue('file')) {
          data.append(
            'file',
            createForm.getFieldValue('file').fileList[0].originFileObj
          )
        }

        return dispatch(createAnswer(data)).then(() => {
          createForm.resetFields()
          return true
        })
      })

    const handleUpdate = async () =>
      await updateForm.validateFields().then(async (result) => {
        const data = new FormData()
        const fields = ['id', 'message']
        fields.map((field) => {
          data.append(field, updateForm.getFieldValue(field) ?? '')
        })
        if (updateForm.getFieldValue('buttons')) {
          updateForm
            .getFieldValue('buttons')
            .forEach((button: any, index: number) => {
              data.append(`buttons[${index}][title]`, button.title)
              data.append(`buttons[${index}][url]`, button.url)
            })
        }

        if (
          updateForm.getFieldValue('file') &&
          updateForm.getFieldValue('file')[0]
        ) {
          data.append(
            'file',
            updateForm.getFieldValue('file')[0].originFileObj
              ? updateForm.getFieldValue('file')[0].originFileObj
              : 'keep-actual-file'
          )
        }
        return dispatch(updateAnswer(data)).then(() => {
          updateForm.resetFields()
          return true
        })
      })

    const handleDelete = async () =>
      await deleteForm.validateFields().then(async (result) =>
        dispatch(deleteAnswer(deleteForm.getFieldsValue())).then(() => {
          deleteForm.resetFields()
          return true
        })
      )

    const handleReset = () => {
      createForm.resetFields()
      updateForm.resetFields()
      deleteForm.resetFields()
    }

    return (
      <>
        {(action === 'create' || action === 'duplicate') && (
          <Create form={createForm} />
        )}
        {action === 'update' && <Update form={updateForm} />}
        {action === 'delete' && <Delete form={deleteForm} />}
        {error && <Alert message={String(error)} type="error" />}
      </>
    )
  }
)

Form.displayName = 'Form'

type FormType = { Form: typeof Form }

export { Form, type FormType }
